import { client, unwrap } from './client';
import { SOAPNoteMetadata, SOAPNoteDetail, RecentNote } from '../types';

function normalizeNoteType(raw: string): string {
  if (!raw) return 'NOTE';
  return raw.replace(/_NOTE$/, '');
}

function normalizeNote(n: any): SOAPNoteMetadata {
  const createdMs = typeof n.createdAt === 'number' ? n.createdAt : Number(n.createdAt) || 0;
  return {
    session_id: n.id ?? '',
    note_type: normalizeNoteType(n.noteType ?? ''),
    created_at: createdMs ? new Date(createdMs).toISOString() : new Date().toISOString(),
    previous_session_id: null,
    case_id: n.caseId ?? null,
    consent_excerpt: n.consent_excerpt,
  };
}

function normalizeNoteDetail(raw: any): SOAPNoteDetail {
  let sections: Record<string, string> = {};
  try {
    sections = typeof raw.noteDetails === 'string'
      ? JSON.parse(raw.noteDetails)
      : (raw.noteDetails ?? {});
  } catch {
    sections = {};
  }
  return {
    session_id: raw.noteId ?? '',
    note_type: normalizeNoteType(raw.noteType ?? ''),
    created_at: raw.createdAt ?? new Date().toISOString(),
    previous_session_id: null,
    case_id: raw.caseId ?? null,
    patient_id: raw.patientId ?? '',
    subjective: sections.subjective ?? '',
    objective: sections.objective ?? '',
    assessment: sections.assessment ?? '',
    plan: sections.plan ?? '',
    treatment: sections.treatment ?? '',
    consent: sections.consent ?? '',
    progress_status: raw.progressStatus ?? '',
  };
}

function normalizeRecentNote(n: any): RecentNote {
  const createdMs = typeof n.createdAt === 'number' ? n.createdAt : Number(n.createdAt) || 0;
  return {
    session_id: n.id ?? '',
    note_type: normalizeNoteType(n.noteType ?? ''),
    created_at: createdMs ? new Date(createdMs).toISOString() : new Date().toISOString(),
    patient_id: n.patientId ?? '',
    patient_name: n.patientName ?? '',
  };
}

export interface NoteListResult {
  items: SOAPNoteMetadata[];
  next_cursor: string | null;
}

export const notesService = {
  async listPatientNotes(patientId: string, _limit = 50): Promise<NoteListResult> {
    const casesRes = await client.get('/note/case-list', { params: { patientId } });
    const cases: any[] = unwrap(casesRes.data) ?? [];
    if (!cases || cases.length === 0) return { items: [], next_cursor: null };

    const allNoteArrays = await Promise.all(
      cases.map((c) =>
        client.get('/note/note-list', { params: { caseId: c.id } })
          .then((res) => (unwrap<any[]>(res.data) ?? []).map(normalizeNote))
          .catch(() => [] as SOAPNoteMetadata[])
      )
    );
    return { items: allNoteArrays.flat(), next_cursor: null };
  },

  async createNote(patientId: string, audioBlob: Blob, noteType: 'INITIAL' | 'FOLLOWUP'): Promise<any> {
    const form = new FormData();
    form.append('patientId', patientId);
    form.append('audioUrl', audioBlob, 'recording.webm');
    form.append('noteType', noteType === 'FOLLOWUP' ? 'FOLLOWUP_NOTE' : 'INITIAL_NOTE');

    if (noteType === 'FOLLOWUP') {
      // Attach to the most recent existing case so the note appears under the same thread.
      // INITIAL notes omit caseId — batman creates a new case automatically.
      try {
        const casesRes = await client.get('/note/case-list', { params: { patientId } });
        const cases: any[] = unwrap(casesRes.data) ?? [];
        if (cases.length > 0) {
          const latest = [...cases].sort((a, b) => Number(b.createdAt) - Number(a.createdAt))[0];
          form.append('caseId', latest.id);
        }
      } catch {
        // Fall through — batman will create a new case if caseId is absent
      }
    }

    const res = await client.post('/note/add-note', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(res.data);
  },

  async getNoteDetail(noteId: string): Promise<SOAPNoteDetail> {
    const res = await client.get('/note', { params: { noteId } });
    const raw = unwrap(res.data);
    return normalizeNoteDetail(raw);
  },

  async chatNote(noteId: string, message: string): Promise<string> {
    const res = await client.post('/patient/chat', { noteId, message });
    const data = unwrap(res.data);
    return data?.response ?? '';
  },

  async listRecentNotes(): Promise<RecentNote[]> {
    const res = await client.get('/user/inProgress-note-list');
    const raw: any[] = unwrap(res.data) ?? [];
    return raw.map(normalizeRecentNote);
  },
};
